import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Building2, Phone, Mail, Globe, MapPin, Key, Server, Shield, FileKey, Users, File, Loader2, ExternalLink } from 'lucide-react';
import { BACKEND_URL } from '@/lib/backend';
import { ClientStatusPills } from '@/components/ClientStatusBadges';

const fetchJson = async (path) => {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
  if (!res.ok) return [];
  return res.json();
};

export default function OrganizationDetailView({ organization, onBack }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    contacts: [],
    passwords: [],
    devices: [],
    domains: [],
    ssl: [],
    licenses: [],
    documents: [],
  });

  const load = useCallback(async () => {
    if (!organization?.id) return;
    setLoading(true);
    const orgId = organization.id;
    const [contacts, passwords, devices, domains, ssl, licenses, documents] = await Promise.all([
      fetchJson(`/api/documentation/contacts?organization_id=${orgId}`),
      fetchJson(`/api/documentation/passwords?organization_id=${orgId}`),
      fetchJson(`/api/documentation/devices?organization_id=${orgId}`),
      fetchJson(`/api/documentation/domains?organization_id=${orgId}`),
      fetchJson(`/api/documentation/ssl-certificates?organization_id=${orgId}`),
      fetchJson(`/api/documentation/licenses?organization_id=${orgId}`),
      fetchJson(`/api/documentation/documents?organization_id=${orgId}`),
    ]);
    setData({ contacts, passwords, devices, domains, ssl, licenses, documents });
    setLoading(false);
  }, [organization?.id]);

  useEffect(() => { load(); }, [load]);

  const sections = [
    { key: 'contacts', title: 'Contacts', icon: Users, color: 'bg-pink-100 text-pink-600', items: data.contacts, render: (c) => (
        <>
          <p className="font-semibold text-slate-800 truncate">{c.name}{c.role ? ` · ${c.role}` : ''}</p>
          <p className="text-xs text-slate-500 truncate">{c.email || c.phone || '—'}</p>
        </>
      ) },
    { key: 'passwords', title: 'Passwords', icon: Key, color: 'bg-purple-100 text-purple-600', items: data.passwords, render: (p) => (
        <>
          <p className="font-semibold text-slate-800 truncate">{p.title || p.name}</p>
          <p className="text-xs text-slate-500 truncate">{p.username || p.url || '—'}</p>
        </>
      ) },
    { key: 'devices', title: 'Devices', icon: Server, color: 'bg-cyan-100 text-cyan-600', items: data.devices, render: (d) => (
        <>
          <p className="font-semibold text-slate-800 truncate">{d.name}</p>
          <p className="text-xs text-slate-500 truncate">{d.type || d.model || '—'}{d.serial_number ? ` · SN ${d.serial_number}` : ''}</p>
        </>
      ) },
    { key: 'domains', title: 'Domains', icon: Globe, color: 'bg-green-100 text-green-600', items: data.domains, render: (d) => (
        <>
          <p className="font-semibold text-slate-800 truncate">{d.domain || d.name}</p>
          <p className="text-xs text-slate-500 truncate">{d.registrar || '—'}{d.expiry_date ? ` · expires ${new Date(d.expiry_date).toLocaleDateString()}` : ''}</p>
        </>
      ) },
    { key: 'ssl', title: 'SSL Certificates', icon: Shield, color: 'bg-teal-100 text-teal-600', items: data.ssl, render: (s) => (
        <>
          <p className="font-semibold text-slate-800 truncate">{s.domain || s.name}</p>
          <p className="text-xs text-slate-500 truncate">{s.issuer || '—'}{s.expiry_date ? ` · expires ${new Date(s.expiry_date).toLocaleDateString()}` : ''}</p>
        </>
      ) },
    { key: 'licenses', title: 'Licenses', icon: FileKey, color: 'bg-amber-100 text-amber-600', items: data.licenses, render: (l) => (
        <>
          <p className="font-semibold text-slate-800 truncate">{l.product || l.name}</p>
          <p className="text-xs text-slate-500 truncate">{l.vendor || '—'}{l.seats ? ` · ${l.seats} seats` : ''}</p>
        </>
      ) },
    { key: 'documents', title: 'Documents', icon: File, color: 'bg-indigo-100 text-indigo-600', items: data.documents, render: (d) => (
        <>
          <p className="font-semibold text-slate-800 truncate">{d.title || d.name}</p>
          <p className="text-xs text-slate-500 truncate">{d.category || d.type || '—'}</p>
        </>
      ) },
  ];

  const totalCount = sections.reduce((s, sec) => s + (sec.items?.length || 0), 0);

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto bg-slate-50" data-testid="organization-detail">
      <Button variant="ghost" onClick={onBack} className="mb-4 text-slate-600 hover:bg-slate-100 -ml-2" data-testid="org-detail-back">
        <ArrowLeft className="w-4 h-4 mr-2" />Back to Organizations
      </Button>

      {/* Header card */}
      <Card className="bg-white border-slate-200 mb-4 md:mb-6">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-start gap-3 md:gap-4 flex-wrap">
            <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-blue-100 text-blue-600 flex items-center justify-center shrink-0">
              <Building2 className="w-6 h-6 md:w-7 md:h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 leading-tight">{organization.name}</h1>
              <ClientStatusPills entity={organization} entityId={organization.id} />
              {organization.description && (
                <p className="text-sm text-slate-600 mt-1">{organization.description}</p>
              )}
              <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs sm:text-sm text-slate-600">
                {organization.phone && <span className="inline-flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" />{organization.phone}</span>}
                {organization.email && <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" />{organization.email}</span>}
                {organization.website && (
                  <a href={organization.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-cyan-600 hover:underline">
                    <Globe className="w-3.5 h-3.5" />{organization.website.replace(/^https?:\/\//, '')}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {organization.address && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{organization.address}</span>}
              </div>
              {organization.notes && (
                <div className="mt-3 p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm text-slate-700 whitespace-pre-wrap">
                  {organization.notes}
                </div>
              )}
            </div>
            <Badge variant="outline" className="border-slate-200 text-slate-600 text-[11px]">{totalCount} item{totalCount !== 1 ? 's' : ''}</Badge>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-cyan-500" /></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {sections.map((sec) => (
            <Card key={sec.key} className="bg-white border-slate-200" data-testid={`org-section-${sec.key}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-slate-800 flex items-center gap-2">
                  <span className={`w-8 h-8 rounded-lg ${sec.color} flex items-center justify-center`}>
                    <sec.icon className="w-4 h-4" />
                  </span>
                  <span>{sec.title}</span>
                  <Badge variant="outline" className="ml-auto border-slate-200 text-slate-500 text-[10px]">{sec.items.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {sec.items.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">No {sec.title.toLowerCase()} on file for this client.</p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {sec.items.slice(0, 8).map((it) => (
                      <li key={it.id} className="py-2.5">{sec.render(it)}</li>
                    ))}
                    {sec.items.length > 8 && (
                      <li className="py-2 text-xs text-slate-500">+{sec.items.length - 8} more</li>
                    )}
                  </ul>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
